import { Fancybox } from "@fancyapps/ui";

const calpak = window.calpak;

const MOBILE_BREAKPOINT = 900;
const THUMB_HEIGHT = 106;
const THUMB_GAP = 12; // Match CSS --thumb-gap value
const THUMB_TOTAL_HEIGHT = THUMB_HEIGHT + THUMB_GAP;
const RESIZE_DEBOUNCE_DELAY = 300;
const VARIANT_UPDATE_DELAY = 200;
const COLOR_SWATCH_UPDATE_DELAY = 300;
const NAVIGATION_UPDATE_RETRY_DELAY = 50;
const NAVIGATION_UPDATE_BUFFER_DELAY = 10;
const DOM_RENDER_DELAY = 100;
const TOLSTOY_INIT_TIMEOUT = 3000;
const isMobile = () => window.innerWidth <= MOBILE_BREAKPOINT;

const updateThumbnailGallery = (delay = VARIANT_UPDATE_DELAY) => {
  if (isMobile()) {
    return;
  }
  
  const thumbnailGallery = document.querySelector('[data-gallery-thumbnails]');
  
  if (thumbnailGallery && thumbnailGallery._galleryInstance) {
    setTimeout(() => {
      thumbnailGallery._galleryInstance.updateForVariant();
    }, delay);
  }
};

class PDPGalleryThumbnails {
  constructor(element) {
    this.element = element;
    this.currentIndex = 0;
    this.isInitialized = false;
    this.fancyboxInstances = []; // Track FancyBox instances for cleanup
    this.tolstoyObservers = []; // Track tolstoy observers for cleanup
    
    // Always cache DOM elements regardless of viewport size
    this.thumbsContainer = element.querySelector('[data-thumbs-container]');
    this.thumbsTrack = element.querySelector('[data-thumbs-track]');
    this.mainGallery = element.querySelector('[data-main-gallery]');
    this.navUp = element.querySelector('[data-thumb-nav="up"]');
    this.navDown = element.querySelector('[data-thumb-nav="down"]');
    this.mainNavPrev = element.querySelector('[data-main-nav="prev"]');
    this.mainNavNext = element.querySelector('[data-main-nav="next"]');
    
    this.handleResize = calpak.debounce(() => {
      if (isMobile() && this.isInitialized) {
        this.destroy();
      } else if (!isMobile() && !this.isInitialized) {
        // CRITICAL: Ensure the old gallery system has marked elements as active first
        if (window.calpak && window.calpak.pdpGalleryUpdate) {
          const pdpGrid = this.element.closest('.pdp__grid');
          if (pdpGrid) {
            const productForm = document.querySelector('form[action*="/cart/add"]');
            const currentVariantId = productForm?.querySelector('[name="id"]')?.value;
            if (currentVariantId) {
              // pdpGalleryUpdate expects a DOM element, not an object
              const variantSelect = productForm?.querySelector('select[name="id"]');
              const optionElement = variantSelect?.querySelector(`option[value="${currentVariantId}"]`);
              
              if (optionElement) {
                window.calpak.pdpGalleryUpdate(pdpGrid, optionElement, false);
              }
            }
          }
        }
        
        setTimeout(() => {
          this.init();
        }, 50);
      }
    }, RESIZE_DEBOUNCE_DELAY);
    
    window.addEventListener('resize', this.handleResize);
    
    if (!isMobile()) {
      this.init();
    }
  }
  
  init() {
    if (this.isInitialized) return;
    
    this.setupThumbnails();
    this.setupMainGallery();
    this.initFancyBox();
    this.bindEvents();
    this.updateNavigation();
    this.watchTolstoyInitialization();
    this.isInitialized = true;
    
    setTimeout(() => {
      if (this.isInitialized) {
        this.updateNavigation();
      }
    }, DOM_RENDER_DELAY);
  }
  
  destroy() {
    this.unbindEvents();
    this.destroyFancyBox();
    this.cleanupTolstoyObservers();
    this.isInitialized = false;
  }
  
  setupThumbnails() {
    // Depends on old gallery system marking elements as active first
    this.thumbs = [...this.thumbsTrack.querySelectorAll('.pdp-thumb-media-thumb.pdp-thumb-media--active')];
    
    if (this.thumbs.length > 0) {
      this.thumbs.forEach(t => {
        t.classList.remove('pdp-gallery-thumbnails__thumb--active');
        if (!t.hasAttribute('tabindex')) {
          t.setAttribute('tabindex', '0');
        }
      });
      this.thumbs[0].classList.add('pdp-gallery-thumbnails__thumb--active');
    }
  }
  
  setupMainGallery() {
    // Get media items that are already marked as active by pdpThumbnailGalleryUpdate
    this.mediaItems = [...this.mainGallery.querySelectorAll('.pdp-thumb-media.pdp-thumb-media--active')];
    
    // Show first active item (like original gallery)
    if (this.mediaItems.length > 0) {
      // Remove any existing gallery active states first
      this.mediaItems.forEach(m => m.classList.remove('pdp-gallery-thumbnails__media--active'));
      
      // Find which thumbnail is currently active and match it to the main gallery
      const activeThumbIndex = [...this.thumbs].findIndex(thumb => 
        thumb.classList.contains('pdp-gallery-thumbnails__thumb--active')
      );
      
      // Use the matching main gallery item, or fall back to first item
      const defaultActiveItem = activeThumbIndex >= 0 && activeThumbIndex < this.mediaItems.length 
        ? this.mediaItems[activeThumbIndex]
        : this.mediaItems[0];
      
      defaultActiveItem.classList.add('pdp-gallery-thumbnails__media--active');
      this.currentIndex = activeThumbIndex >= 0 ? activeThumbIndex : 0;
    }
  }
  
  bindEvents() {
    // Clear any existing event listeners to prevent duplicates
    this.unbindEvents();
    
    // Store bound functions for later cleanup
    this.boundFunctions = {
      thumbClicks: [],
      navUp: () => this.scrollThumbnails('up'),
      navDown: () => this.scrollThumbnails('down'),
      mainNavPrev: () => this.navigate('prev'),
      mainNavNext: () => this.navigate('next'),
      // Simple keyboard navigation - only essentials
      mainKeydown: (e) => {
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          this.navigate('prev');
        }
        if (e.key === 'ArrowRight') {
          e.preventDefault();
          this.navigate('next');
        }
      }
    };
    
    // Thumbnail clicks - simple click and Enter/Space handling
    this.thumbs.forEach((thumb, index) => {
      const clickHandler = () => this.goToSlide(index);
      const keydownHandler = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.goToSlide(index);
        }
      };
      
      thumb.addEventListener('click', clickHandler);
      thumb.addEventListener('keydown', keydownHandler);
      
      // Store for cleanup
      this.boundFunctions.thumbClicks.push({ element: thumb, handler: clickHandler });
      this.boundFunctions.thumbClicks.push({ element: thumb, handler: keydownHandler });
    });
    
    // Thumbnail navigation
    if (this.navUp) {
      this.navUp.addEventListener('click', this.boundFunctions.navUp);
    }
    if (this.navDown) {
      this.navDown.addEventListener('click', this.boundFunctions.navDown);
    }
    
    // Main gallery navigation
    if (this.mainNavPrev) {
      this.mainNavPrev.addEventListener('click', this.boundFunctions.mainNavPrev);
    }
    if (this.mainNavNext) {
      this.mainNavNext.addEventListener('click', this.boundFunctions.mainNavNext);
    }
    
    // Simple keyboard navigation for main gallery
    if (this.mainGallery) {
      this.mainGallery.addEventListener('keydown', this.boundFunctions.mainKeydown);
    }
  }
  
  unbindEvents() {
    if (!this.boundFunctions) return;
    
    // Remove thumbnail event listeners
    this.boundFunctions.thumbClicks.forEach(({ element, handler }) => {
      element.removeEventListener('click', handler);
      element.removeEventListener('keydown', handler);
    });
    
    // Remove navigation event listeners
    if (this.navUp && this.boundFunctions.navUp) {
      this.navUp.removeEventListener('click', this.boundFunctions.navUp);
    }
    if (this.navDown && this.boundFunctions.navDown) {
      this.navDown.removeEventListener('click', this.boundFunctions.navDown);
    }
    if (this.mainNavPrev && this.boundFunctions.mainNavPrev) {
      this.mainNavPrev.removeEventListener('click', this.boundFunctions.mainNavPrev);
    }
    if (this.mainNavNext && this.boundFunctions.mainNavNext) {
      this.mainNavNext.removeEventListener('click', this.boundFunctions.mainNavNext);
    }
    if (this.mainGallery && this.boundFunctions.mainKeydown) {
      this.mainGallery.removeEventListener('keydown', this.boundFunctions.mainKeydown);
    }
  }
  
  goToSlide(index) {
    if (index < 0 || index >= this.mediaItems.length) return;
    
    // Update active states
    this.thumbs.forEach(t => t.classList.remove('pdp-gallery-thumbnails__thumb--active'));
    this.mediaItems.forEach(m => m.classList.remove('pdp-gallery-thumbnails__media--active'));
    
    if (this.thumbs[index]) {
      this.thumbs[index].classList.add('pdp-gallery-thumbnails__thumb--active');
    }
    if (this.mediaItems[index]) {
      this.mediaItems[index].classList.add('pdp-gallery-thumbnails__media--active');
    }
    
    this.currentIndex = index;
    this.ensureThumbVisible(index);
    this.updateNavigation();
  }
  
  navigate(direction) {
    const newIndex = direction === 'next' ? 
      Math.min(this.currentIndex + 1, this.mediaItems.length - 1) : 
      Math.max(this.currentIndex - 1, 0);
    this.goToSlide(newIndex);
  }
  
  scrollThumbnails(direction) {
    if (!this.thumbsTrack || !this.thumbsContainer) return;
    
    const currentTransform = this.getTransformY(this.thumbsTrack);
    const scrollAmount = THUMB_TOTAL_HEIGHT * 3; // Scroll 3 items at a time
    
    let newTransform;
    if (direction === 'up') {
      newTransform = Math.min(currentTransform + scrollAmount, 0);
    } else {
      const maxScroll = -(this.thumbsTrack.scrollHeight - this.thumbsContainer.offsetHeight);
      newTransform = Math.max(currentTransform - scrollAmount, maxScroll);
    }
    
    this.thumbsTrack.style.transform = `translateY(${newTransform}px)`;
    
    // Update navigation using the new transform value directly
    // instead of waiting for CSS transition to complete
    this.updateNavigationWithTransform(newTransform);
  }
  
  ensureThumbVisible(index) {
    if (!this.thumbsContainer || !this.thumbsTrack || index >= this.thumbs.length) return;
    
    const containerHeight = this.thumbsContainer.offsetHeight;
    const thumbTop = index * THUMB_TOTAL_HEIGHT;
    const thumbBottom = thumbTop + THUMB_HEIGHT;
    const currentTransform = Math.abs(this.getTransformY(this.thumbsTrack));
    const viewportTop = currentTransform;
    const viewportBottom = currentTransform + containerHeight;
    
    if (thumbTop < viewportTop) {
      this.thumbsTrack.style.transform = `translateY(-${thumbTop}px)`;
    } else if (thumbBottom > viewportBottom) {
      this.thumbsTrack.style.transform = `translateY(-${thumbBottom - containerHeight}px)`;
    }
  }
  
  updateNavigation() {
    // Update main navigation button states
    if (this.mainNavPrev) {
      this.mainNavPrev.disabled = this.currentIndex === 0;
    }
    if (this.mainNavNext) {
      this.mainNavNext.disabled = this.currentIndex === this.mediaItems.length - 1;
    }
    
    // Update thumb navigation
    this.updateThumbNavigation();
  }
  
  updateNavigationWithTransform(knownTransform) {
    // Update main navigation button states
    if (this.mainNavPrev) {
      this.mainNavPrev.disabled = this.currentIndex === 0;
    }
    if (this.mainNavNext) {
      this.mainNavNext.disabled = this.currentIndex === this.mediaItems.length - 1;
    }
    
    // Update thumb navigation with known transform value
    this.updateThumbNavigation(knownTransform);
  }
  
  updateThumbNavigation(providedTransform = null) {
    if (!this.navUp || !this.navDown) return;
    
    // Ensure dimensions are calculated properly - use setTimeout if needed for auto-retry
    const updateLogic = () => {
      const currentTransform = providedTransform !== null ? providedTransform : this.getTransformY(this.thumbsTrack);
      const containerHeight = this.thumbsContainer.offsetHeight;
      const trackHeight = this.thumbsTrack.scrollHeight;
      
      // Add extra checks to ensure proper dimensions
      if (containerHeight === 0 || trackHeight === 0) {
        // Only retry if we don't have a provided transform (avoid infinite recursion)
        if (providedTransform === null) {
          setTimeout(() => this.updateThumbNavigation(), NAVIGATION_UPDATE_RETRY_DELAY);
        }
        return;
      }
      
      // Disable up button if we're at or above the top
      this.navUp.disabled = currentTransform >= 0;
      
      // Disable down button if we can't scroll further down
      // Add a small buffer (2px) to handle rounding issues
      const maxScroll = trackHeight - containerHeight;
      this.navDown.disabled = Math.abs(currentTransform) >= maxScroll - 2;
      
      // If there's not enough content to scroll, disable both buttons
      if (trackHeight <= containerHeight) {
        this.navUp.disabled = true;
        this.navDown.disabled = true;
      }
    };
    
    // Run immediately
    updateLogic();
    
    // Only run with delay if we don't have a provided transform (for timing issues)
    if (providedTransform === null) {
      setTimeout(updateLogic, NAVIGATION_UPDATE_BUFFER_DELAY);
    }
  }
  
  getTransformY(element) {
    const transform = window.getComputedStyle(element).transform;
    if (transform === 'none') return 0;
    const matrix = transform.match(/matrix.*\((.+)\)/)[1].split(', ');
    return parseFloat(matrix[5]) || 0;
  }
  
  initFancyBox() {
    // Clean up existing instances first
    this.destroyFancyBox();
    
    // Simple FancyBox setup - let it handle its own accessibility
    const generalGalleryElements = this.mainGallery.querySelectorAll('[data-fancybox^="gallery"]');
    if (generalGalleryElements.length > 0) {
      const generalInstance = Fancybox.bind('[data-fancybox="gallery"]');
      if (generalInstance) this.fancyboxInstances.push(generalInstance);
    }

    // Get unique colors for color-specific galleries
    const mediaColors = [...this.mainGallery.querySelectorAll(".pdp-thumb-media")];
    const mediaUniqueColors = [...new Set(mediaColors.map(ele => ele.dataset.variants))];

    mediaUniqueColors.forEach(color => {
      if (!color?.length) return;
      
      const fancyboxSelector = `[data-fancybox="gallery-desktop-${color}"]`;
      const colorInstance = Fancybox.bind(fancyboxSelector);
      if (colorInstance) this.fancyboxInstances.push(colorInstance);
    });
  }

  destroyFancyBox() {
    // Clean up all FancyBox instances
    this.fancyboxInstances.forEach(instance => {
      if (instance && typeof instance.destroy === 'function') {
        instance.destroy();
      }
    });
    this.fancyboxInstances = [];
  }
  
  // Method to update gallery when variant changes (called from variants.js)
  watchTolstoyInitialization() {
    // Clean up any existing observers first
    this.cleanupTolstoyObservers();
    
    // Use a single observer watching the main gallery container
    // instead of creating multiple observers for each tolstoy element
    const observer = new MutationObserver((mutations) => {
      let shouldResetup = false;
      
      // Check all mutations for tolstoy initialization changes
      mutations.forEach(mutation => {
        // Check if any tolstoy-carousel elements were modified or added
        const checkTolstoyElements = (nodes) => {
          nodes.forEach(node => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              // Check if this node is a tolstoy-carousel or contains one
              const tolstoyCarousels = node.matches && node.matches('tolstoy-carousel') 
                ? [node] 
                : node.querySelectorAll ? [...node.querySelectorAll('tolstoy-carousel')] : [];
              
              tolstoyCarousels.forEach(tolstoyCarousel => {
                const container = tolstoyCarousel.closest('.pdp-thumb-media--first-prod-video');
                if (container && !container.classList.contains('tolstoy-initialized')) {
                  if (this.isTolstoyInitialized(tolstoyCarousel)) {
                    container.classList.add('tolstoy-initialized');
                    shouldResetup = true;
                  }
                }
              });
            }
          });
        };
        
        // Check added nodes
        if (mutation.addedNodes.length > 0) {
          checkTolstoyElements(mutation.addedNodes);
        }
        
        // Check attribute changes on tolstoy elements
        if (mutation.type === 'attributes' && 
            mutation.target.matches && 
            mutation.target.matches('tolstoy-carousel') &&
            mutation.attributeName === 'data-status') {
          const container = mutation.target.closest('.pdp-thumb-media--first-prod-video');
          if (container && !container.classList.contains('tolstoy-initialized')) {
            if (this.isTolstoyInitialized(mutation.target)) {
              container.classList.add('tolstoy-initialized');
              shouldResetup = true;
            }
          }
        }
      });
      
      // Only re-setup if we detected tolstoy initialization
      if (shouldResetup) {
        this.setupMainGallery();
      }
    });
    
    // Track the single observer
    this.tolstoyObservers.push(observer);
    
    // Observe the main gallery with comprehensive options
    observer.observe(this.mainGallery, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-status']
    });
    
    // Initial check for already initialized tolstoy elements
    const tolstoyContainers = this.mainGallery.querySelectorAll('.pdp-thumb-media--first-prod-video');
    tolstoyContainers.forEach(container => {
      const tolstoyCarousel = container.querySelector('tolstoy-carousel');
      if (tolstoyCarousel && this.isTolstoyInitialized(tolstoyCarousel)) {
        container.classList.add('tolstoy-initialized');
      }
    });
    
    // Fallback timeout to ensure all containers are marked as initialized
    setTimeout(() => {
      const uninitializedContainers = this.mainGallery.querySelectorAll('.pdp-thumb-media--first-prod-video:not(.tolstoy-initialized)');
      uninitializedContainers.forEach(container => {
        container.classList.add('tolstoy-initialized');
      });
      if (uninitializedContainers.length > 0) {
        this.setupMainGallery();
      }
    }, TOLSTOY_INIT_TIMEOUT);
  }
  
  cleanupTolstoyObservers() {
    // Disconnect and clear all tolstoy observers
    this.tolstoyObservers.forEach(observer => {
      observer.disconnect();
    });
    this.tolstoyObservers = [];
  }
  
  isTolstoyInitialized(tolstoyCarousel) {
    // Check multiple indicators that tolstoy has initialized
    return (
      tolstoyCarousel.getAttribute('data-status') === 'loaded' ||
      tolstoyCarousel.querySelector('.tolstoy-carousel-container') ||
      tolstoyCarousel.querySelector('[data-tolstoy-element]') ||
      tolstoyCarousel.children.length > 0
    );
  }
  
  updateForVariant() {
    // Re-initialize thumbnails and media items
    this.setupThumbnails();
    this.setupMainGallery();
    // Re-initialize FancyBox for new DOM elements
    this.initFancyBox();
    // IMPORTANT: Re-bind events because DOM elements may have been recreated/modified
    this.bindEvents();
    this.currentIndex = 0;
    this.updateNavigation();
    // Re-watch tolstoy initialization for new variant
    this.watchTolstoyInitialization();
  }
}

// Always create instances but only initialize on desktop
document.addEventListener('calpak:loaded', () => {
  const galleries = document.querySelectorAll('[data-gallery-thumbnails]');
  galleries.forEach(gallery => {
    const instance = new PDPGalleryThumbnails(gallery);
    // Always store instance, regardless of viewport size
    gallery._galleryInstance = instance;
  });
  
  // Integration with existing variant change system (desktop only)
  // Store original pdpGalleryUpdate if it exists
  if (window.calpak && window.calpak.pdpGalleryUpdate) {
    const originalUpdate = window.calpak.pdpGalleryUpdate;
    
    // Override with our enhanced version (desktop only)
    window.calpak.pdpGalleryUpdate = function(...args) {
      // Call original function first
      originalUpdate.apply(this, args);
      
      // Only update thumbnail gallery on desktop
      updateThumbnailGallery();
    };
  }
  
  // Also listen for color swatch clicks directly (desktop only)
  const colorSwatches = document.querySelectorAll('.color-swatch');
  colorSwatches.forEach(swatch => {
    swatch.addEventListener('click', () => {
      // Delay to allow variant update to complete first
      updateThumbnailGallery(COLOR_SWATCH_UPDATE_DELAY);
    });
  });
}); 